import { BigNumber } from 'ethers';
import { useCallback } from 'react';
import { Skeleton, Text } from '@tectonicfi/tectonic-ui-kit';
import { PageSection } from '@tectonicfi/tectonic-ui-kit';
import { TectonicAsset } from '@cronos-labs/tectonic-sdk';

import { formatMarketsSummaryUsdValue } from '@lib/units';
import { getNormalizedUsdPrice, getUnderlyingUsdValue } from '@lib/math';
import useAppSelector from '@hooks/useAppSelector';
import useSdkAndSupportedAssets from '@hooks/useSdkAndSupportedAssets';

import MarketsSummary from '../MarketsSummary';
import useMarketsPageData from '../useMarketsPageData';
import Header from '../Header';

import Description from './Description';
import MarketsTable from './MarketsTable';

function MarketsPageView(): JSX.Element {
  const { borrowAmountsOfVENOPool, supplyAmountsOfVENOPool, usdPrices } =
    useAppSelector((state) => ({
      borrowAmountsOfVENOPool: state.venoMarkets.borrowAmounts,
      supplyAmountsOfVENOPool: state.venoMarkets.supplyAmounts,
      usdPrices: state.usdPrices.bySymbol,
    }));

  const { list: supportedVENOPoolAssets } = useSdkAndSupportedAssets('VENO');

  const {
    data: venoPoolData,
    updateNetBorrowApy: updateNetBorrowApyVENO,
    updateNetSupplyApy: updateNetSupplyApyVENO,
  } = useMarketsPageData(supportedVENOPoolAssets, 'VENO');

  const getUpdateNetBorrowApy = useCallback(
    (symbol: string, netApy: number) => {
      updateNetBorrowApyVENO(symbol, netApy);
    },
    [updateNetBorrowApyVENO]
  );

  const getUpdateNetSupplyApy = useCallback(
    (symbol: string, netApy: number) => {
      updateNetSupplyApyVENO(symbol, netApy);
    },
    [updateNetSupplyApyVENO]
  );

  const getBorrowSupplyInUsd = useCallback(
    (
      amounts: Record<string, string | undefined>,
      supportedAssetsLists: TectonicAsset[]
    ) => {
      if (
        Object.keys(amounts).length < supportedAssetsLists.length ||
        Object.keys(usdPrices).length < supportedAssetsLists.length
      ) {
        return null;
      }

      return supportedAssetsLists.reduce((accumulator, asset) => {
        const assetAmount = amounts[asset.symbol];
        const assetUsdPrice = usdPrices[asset.symbol];

        if (assetAmount && assetUsdPrice) {
          const assetUsdValue = getNormalizedUsdPrice(
            asset,
            getUnderlyingUsdValue(
              asset,
              BigNumber.from(assetAmount),
              BigNumber.from(assetUsdPrice)
            )
          );

          return accumulator.add(assetUsdValue);
        }

        return accumulator;
      }, BigNumber.from(0));
    },
    [usdPrices]
  );

  const totalBorrowInUsdOfVENOPool: Null<BigNumber> = getBorrowSupplyInUsd(
    borrowAmountsOfVENOPool,
    supportedVENOPoolAssets
  );

  const totalSupplyInUsdOfVENOPool: Null<BigNumber> = getBorrowSupplyInUsd(
    supplyAmountsOfVENOPool,
    supportedVENOPoolAssets
  );

  return (
    <PageSection>
      <Header mode="VENO" />

      <MarketsSummary
        borrowLabel="Veno Pool borrow"
        supplyLabel="Veno Pool supply"
        totalBorrow={
          totalBorrowInUsdOfVENOPool ? (
            <Text className="text-medium font-medium">
              {formatMarketsSummaryUsdValue(totalBorrowInUsdOfVENOPool)}
            </Text>
          ) : (
            <Skeleton className="w-1/2">
              <Text className="text-medium text-transparent">.</Text>
            </Skeleton>
          )
        }
        totalSupply={
          totalSupplyInUsdOfVENOPool ? (
            <Text className="text-medium font-medium">
              {formatMarketsSummaryUsdValue(totalSupplyInUsdOfVENOPool)}
            </Text>
          ) : (
            <Skeleton className="w-1/2">
              <Text className="text-medium text-transparent">.</Text>
            </Skeleton>
          )
        }
      />

      <Description />

      <MarketsTable
        data={venoPoolData}
        supportedAssets={supportedVENOPoolAssets}
        updateNetBorrowApy={getUpdateNetBorrowApy}
        updateNetSupplyApy={getUpdateNetSupplyApy}
      />
    </PageSection>
  );
}

export default MarketsPageView;
